/**
 * An input box's "Fill from data": the box starts with a value from the
 * backend - one column of a dataset, summarised (SUM of col_2025_volume, …) and
 * filtered by the dashboard's dropdowns - instead of a fixed default. The user
 * can still type over it. Stored on `widget.binding` as `valueDataset` /
 * `valueField` / `valueAggregation`.
 */
import type { DesignNode } from '../design-builder.types';

export interface InputDataBinding {
  dataset: string;
  field: string;
  aggregation: string;
}

/** The node's "Fill from data" binding, or null when none is set. */
/** The binding keys of an input box's "Fill from data" (see inputDataBindingOf); publishing must keep them. */
export const INPUT_DATA_BINDING_KEYS = ['valueDataset', 'valueField', 'valueAggregation'] as const;

/**
 * Everything an input box keeps on its binding through save, reload and publish: the
 * "Fill from data" keys that are set, and its "Save button (update)" settings (`save`).
 */
export function inputBoxBindingKeys(binding: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  INPUT_DATA_BINDING_KEYS.forEach((key) => {
    if (typeof binding[key] === 'string' && binding[key]) out[key] = binding[key];
  });
  const save = binding['save'];
  if (save && typeof save === 'object' && !Array.isArray(save)) out['save'] = { ...(save as Record<string, unknown>) };
  return out;
}

export function inputDataBindingOf(node: DesignNode | null | undefined): InputDataBinding | null {
  if (node?.widget?.layoutType !== 'INPUT_BOX') return null;
  const binding = (node.widget.binding ?? {}) as Record<string, unknown>;
  const dataset = typeof binding['valueDataset'] === 'string' ? binding['valueDataset'] : '';
  const field = typeof binding['valueField'] === 'string' ? binding['valueField'] : '';
  if (!dataset || !field) return null;
  const aggregation = typeof binding['valueAggregation'] === 'string' && binding['valueAggregation'] ? binding['valueAggregation'] : 'SUM';
  return { dataset, field, aggregation };
}

/** The componentId the compute API registers this value under. */
export function inputDataComponentId(nodeId: string): string {
  return `CMP-INPUT-DATA-${nodeId}`;
}
